import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import { fmtDate } from 'src/app/_helpers/utils';
import { DocumentService } from 'src/app/_services/document.service';
import { DocumentSummary } from 'src/app/_types/document.types';
import { ComboboxComponent } from 'src/app/shared/components/combobox/combobox.component';
import {
  DOCUMENT_COMBOBOX_STYLES,
  DOCUMENT_COMBOBOX_TEMPLATE,
  DocumentComboboxBase,
} from 'src/app/shared/components/document-combobox/document-combobox.base';
import { DocumentUploadModalComponent } from 'src/app/shared/components/document-upload-modal/document-upload-modal.component';

/** Picking it lands `null` in the control; see `DocumentComboboxBase.sentinel`. */
export const NO_DOCUMENT_SUMMARY = {
  id: -1,
  docName: '',
  type: null,
  createdOn: '',
  isPublished: false,
  flowDisplayId: null,
  policyId: null,
} as unknown as DocumentSummary;

/**
 * Single-select over one customer's stored documents of one type — the
 * one-document sibling of `app-document-picker`, shaped like
 * `app-signed-contract-document-combobox` but fed by document-store's search.
 */
@Component({
  selector: 'app-document-summary-combobox',
  standalone: true,
  imports: [ComboboxComponent, DocumentUploadModalComponent],
  template: DOCUMENT_COMBOBOX_TEMPLATE,
  styles: [DOCUMENT_COMBOBOX_STYLES],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentSummaryComboboxComponent extends DocumentComboboxBase<DocumentSummary> {
  private readonly documentService = inject(DocumentService);

  /** Narrows to one insured person; empty means the whole household. */
  readonly memberId = input<string | null>(null);

  readonly type = input.required<DocumentType>();

  /** Drops the `noneLabel` entry: the host wants a real pick. */
  readonly required = input(false);

  protected readonly sentinel = NO_DOCUMENT_SUMMARY;

  constructor() {
    super();
    this.connect(
      combineLatest({
        userId: toObservable(this.userId).pipe(distinctUntilChanged()),
        memberId: toObservable(this.memberId).pipe(distinctUntilChanged()),
        type: toObservable(this.type).pipe(distinctUntilChanged()),
      }),
      ({ userId, memberId, type }, query) =>
        userId
          ? this.documentService
              .searchDocuments({
                userId,
                memberId: memberId ?? undefined,
                type,
                query: query.trim() || undefined,
                limit: this.limit(),
              })
              .pipe(map((response) => response.data?.documents ?? []))
          : null
    );
  }

  protected isRequired(): boolean {
    return this.required();
  }

  protected searchReady(): boolean {
    return !!this.userId();
  }

  protected uploadMemberId(): string | null {
    return this.memberId();
  }

  protected uploadType(): DocumentType {
    return this.type();
  }

  protected itemId(document: DocumentSummary): number {
    return document.id;
  }

  protected itemTitle(document: DocumentSummary): string {
    return document.docName;
  }

  protected itemDetails(document: DocumentSummary): string {
    return fmtDate(document.createdOn);
  }
}
